// Small shared formatters for the OMS console.
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

const IST = "Asia/Kolkata";

// Render a backend timestamp in IST, regardless of the viewer's browser timezone. The OMS backend
// emits UTC timestamps and the ops team works in IST, so every time on the console must read IST —
// left to the browser it renders in whatever zone the machine is set to (which is how UTC leaked
// onto the Operations board). Parsing via `dayjs.utc` pins the instant correctly whether the string
// is `Z`-suffixed, carries an offset, or is naive-UTC; `.tz(IST)` then formats the IST wall time.
export const fmtIST = (
  v: string | number | Date | null | undefined,
  fmt = "DD MMM HH:mm"
): string => {
  if (v === null || v === undefined || v === "") return "—";
  const d = dayjs.utc(v);
  return d.isValid() ? d.tz(IST).format(fmt) : "—";
};

// Render-safe text for a value the backend types as a string but derives from a THIRD PARTY (Zoho /
// Loginext error envelopes, provider status text). React throws on an object child and, with nothing
// to contain it, that unmounts the whole page — a Loginext reject object in a checklist row's `error`
// is exactly how the order screen once went blank. So anything of third-party provenance goes through
// here: a string passes untouched, anything else is serialised so it stays READABLE instead of fatal.
export const fmtText = (v: unknown): string => {
  if (v === null || v === undefined) return "";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
};

// A Google Maps search link for a place we only know by address. Takes the address pieces we have,
// drops blanks, and builds a `?query=` search URL. Only a fallback now — prefer `googleMapsUrl`,
// which uses the real lat/long we store when it's present. Returns null when there's nothing to
// search on.
export const googleMapsSearchUrl = (
  parts: Array<string | null | undefined>
): string | null => {
  const query = parts.map((p) => (p ?? "").toString().trim()).filter(Boolean).join(", ");
  if (!query) return null;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
};

// A Google Maps link that prefers the PRECISE lat/long our system stores for the location (address
// rows and every logistics endpoint snapshot carry coordinates), dropping a pin exactly on it. Only
// when coordinates are missing does it fall back to a fuzzy address-text search. Returns null when we
// have neither. Use this everywhere a location link is shown.
export const googleMapsUrl = (
  coords: { lat?: number | null; long?: number | null } | null | undefined,
  parts: Array<string | null | undefined>
): string | null => {
  const lat = coords?.lat;
  const long = coords?.long;
  if (lat != null && long != null) {
    return `https://www.google.com/maps/search/?api=1&query=${lat},${long}`;
  }
  return googleMapsSearchUrl(parts);
};

// Trim trailing-zero decimals so a whole quantity reads "3" not "3.000", while a
// genuine fraction ("2.500" -> "2.5") is preserved. Non-numeric/empty falls back to "—".
export const fmtQty = (v: string | number | null | undefined): string => {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  return Number.isNaN(n) ? String(v) : String(n);
};

// Quantity with its unit of measure appended ("10 sqft", "3 bag"). Falls back to the bare quantity
// when the unit is unknown, and never appends a unit to the "—" placeholder.
export const fmtQtyUnit = (
  v: string | number | null | undefined,
  unit?: string | null
): string => {
  const q = fmtQty(v);
  return unit && q !== "—" ? `${q} ${unit}` : q;
};

// The order-detail route, optionally deep-linked to ONE allotment on it — that card then opens
// expanded and scrolls itself into view (see components/allocation_card.tsx).
//
// Two ways to name the leg, because the two boards hold different keys: the PO board has the
// allocation id (always present, even before a PO number is raised), the Operations board has
// only the PO number(s) on the truck. `allocationId` wins when both are given.
//
// Callers pass a PO number only when the row means exactly ONE allotment — a consolidated truck
// carries several, and there is no single card to send the operator to.
export const omsOrderPath = (
  salesOrderId: number | string,
  target?: { allocationId?: number | null; poNumber?: string | null }
): string => {
  const base = `/oms/${salesOrderId}`;
  if (target?.allocationId) return `${base}?allocation=${target.allocationId}`;
  if (target?.poNumber) return `${base}?po=${encodeURIComponent(target.poNumber)}`;
  return base;
};
